import { hasPermissionsSaga } from 'features/permissions/sagas/hasPermissionsSaga';
import { PermissionTypes } from 'features/permissions/types';
import { RoomStatus } from 'features/room/types';
import { selectIsAudioOnlyRoom, selectRoomStatus } from 'features/room/roomSlice';
import { muteAudioSaga } from 'features/streaming/sagas/muteAudioSaga';
import { muteVideoSaga } from 'features/streaming/sagas/muteVideoSaga';
import { toggleLocalVideoSaga } from 'features/streaming/sagas/toggleLocalVideoSaga';
import { unmuteAudioSaga } from 'features/streaming/sagas/unmuteAudioSaga';
import { unmuteVideoSaga } from 'features/streaming/sagas/unmuteVideoSaga';
import { call, put, select } from 'redux-saga/effects';
import { BroadcastMessageTypes, eventBus } from 'utils/eventBus';
import { toggleLocalAudioSaga } from 'features/streaming/sagas/toggleLocalAudioSaga';
import { joinDefaultsChanged, selectJoinMediaDefaults } from 'features/user-media/userMediaSlice';
import { JoinMediaDefaults } from 'features/user-media/types';

export const videoCommands = ['enableVideo', 'toggleVideo'] as BroadcastMessageTypes[];
export const audioCommands = [
  'enableAudio',
  'disableAudio',
  'toggleAudio',
] as BroadcastMessageTypes[];

function* storeBroadcastIntentSaga(command: BroadcastMessageTypes) {
  const defaults: JoinMediaDefaults = yield select(selectJoinMediaDefaults);

  // initState is the source of truth for SDK intent: it survives the authorizeUser.fulfilled
  // join-defaults rewrite, and its synchronous writes keep concurrent workers convergent
  const next = {
    audio: eventBus.initState.audioEnabled ?? defaults.audio,
    video: eventBus.initState.videoEnabled ?? defaults.video,
  };

  switch (command) {
    case 'enableVideo':
    case 'disableVideo':
    case 'toggleVideo':
      next.video = command === 'toggleVideo' ? !next.video : command === 'enableVideo';
      eventBus.initState.videoEnabled = next.video;
      break;
    case 'enableAudio':
    case 'disableAudio':
    case 'toggleAudio':
      next.audio = command === 'toggleAudio' ? !next.audio : command === 'enableAudio';
      eventBus.initState.audioEnabled = next.audio;
      break;
    default:
      return;
  }

  yield put(joinDefaultsChanged(next));
}

export function* onControlBroadcastCommandSaga(command: BroadcastMessageTypes) {
  const roomStatus: RoomStatus = yield select(selectRoomStatus);

  if (roomStatus !== 'joined') {
    yield call(storeBroadcastIntentSaga, command);
    return;
  }

  const canBroadcastVideo: boolean = yield call(hasPermissionsSaga, PermissionTypes.broadcastVideo);
  const canBroadcastAudio: boolean = yield call(hasPermissionsSaga, PermissionTypes.broadcastAudio);
  const canBroadcast = canBroadcastVideo || canBroadcastAudio;

  if (!canBroadcast) {
    yield call(
      eventBus.forbiddenActionError,
      'Forbidden action. This participant is not allowed to broadcast',
      'broadcast'
    );

    return;
  }

  const isAudioOnly: boolean = yield select(selectIsAudioOnlyRoom);

  if (isAudioOnly && videoCommands.includes(command)) {
    yield call(
      eventBus.notAllowedError,
      'Video broadcast is not available in audio-only rooms',
      'broadcast'
    );
    return;
  }

  if (videoCommands.includes(command) && !canBroadcastVideo) {
    yield call(
      eventBus.forbiddenActionError,
      'Forbidden action. This participant is not allowed to broadcast video',
      'broadcast'
    );
    return;
  }

  if (audioCommands.includes(command) && !canBroadcastAudio) {
    yield call(
      eventBus.forbiddenActionError,
      'Forbidden action. This participant is not allowed to broadcast audio',
      'broadcast'
    );
    return;
  }

  switch (command) {
    case 'enableVideo':
      yield call(unmuteVideoSaga);
      break;
    case 'enableAudio':
      yield call(unmuteAudioSaga);
      break;
    case 'disableVideo':
      yield call(muteVideoSaga);
      break;
    case 'disableAudio':
      yield call(muteAudioSaga);
      break;
    case 'toggleVideo':
      yield call(toggleLocalVideoSaga);
      break;
    case 'toggleAudio':
      yield call(toggleLocalAudioSaga);
      break;

    default:
      break;
  }
}
